import { useQuery } from "@tanstack/react-query";

import { getPlatformDashboard } from "@/api/platform";
import { ChurchesNeedingAttentionCard } from "@/components/platform/dashboard/ChurchesNeedingAttentionCard";
import { OnboardingFunnelCard } from "@/components/platform/dashboard/OnboardingFunnelCard";
import { PendingRequestsCard } from "@/components/platform/dashboard/PendingRequestsCard";
import { PilotHealthCard } from "@/components/platform/dashboard/PilotHealthCard";
import { RecentActivityCard } from "@/components/platform/dashboard/RecentActivityCard";
import { StatsRow } from "@/components/platform/dashboard/StatsRow";

export function PlatformDashboardPage() {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["platform-dashboard"],
    queryFn: getPlatformDashboard,
  });

  if (isLoading) {
    return <PlatformDashboardLoadingState />;
  }

  if (isError || !data) {
    return (
      <PlatformDashboardErrorState
        message={
          error instanceof Error
            ? error.message
            : "The platform dashboard could not be loaded."
        }
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-600">
          Platform Console
        </p>

        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
          Pilot operations
        </h1>

        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
          Review pilot demand, monitor onboarding, and manage
          MinistryIQ churches from one place.
        </p>
      </div>

      <StatsRow kpis={data.kpis} />

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <OnboardingFunnelCard
          stages={data.request_funnel}
        />

        <RecentActivityCard
          activity={data.recent_activity}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <PendingRequestsCard
          requests={data.recent_requests}
        />

        <PilotHealthCard />
      </div>

      <ChurchesNeedingAttentionCard />
    </div>
  );
}

function PlatformDashboardLoadingState() {
  return (
    <div className="space-y-6">
      <div className="animate-pulse">
        <div className="h-4 w-36 rounded bg-slate-200" />
        <div className="mt-3 h-9 w-72 rounded bg-slate-200" />
        <div className="mt-3 h-4 w-full max-w-2xl rounded bg-slate-100" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="h-32 animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="h-4 w-24 rounded bg-slate-200" />
            <div className="mt-5 h-8 w-16 rounded bg-slate-200" />
            <div className="mt-3 h-3 w-32 rounded bg-slate-100" />
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-96 animate-pulse rounded-2xl border border-slate-200 bg-white shadow-sm"
          />
        ))}
      </div>
    </div>
  );
}

interface PlatformDashboardErrorStateProps {
  message: string;
  onRetry: () => void;
}

function PlatformDashboardErrorState({
  message,
  onRetry,
}: PlatformDashboardErrorStateProps) {
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8">
      <p className="text-lg font-semibold text-rose-950">
        Unable to load the Platform Console
      </p>

      <p className="mt-2 text-sm leading-6 text-rose-700">
        {message}
      </p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-5 rounded-lg bg-rose-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-600"
      >
        Try again
      </button>
    </div>
  );
}